import React from 'react';
import {
  Box, Stack, TextField, Typography, IconButton, Button, Paper, FormControlLabel, Switch,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { useImagePicker } from './MediaPicker';

// A friendly, recursive form generated from a section's actual content object.
// Non-technical users get labeled text/textarea/toggle inputs, image previews,
// and add/remove for repeatable lists — never JSON. Edits are immutable.

const ACRONYMS: Record<string, string> = {
  url: 'URL', cta: 'CTA', seo: 'SEO', mfa: 'MFA', sso: 'SSO', iam: 'IAM', id: 'ID', faq: 'FAQ',
};
function humanize(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map((w) => ACRONYMS[w.toLowerCase()] ?? w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const isImageKey = (k: string) => /image|icon|logo|avatar|photo|thumb|bg/i.test(k);
const isLongKey = (k: string) =>
  /description|content|body|review|excerpt|message|quote|text|answer|summary/i.test(k);

function emptyLike(v: unknown): unknown {
  if (Array.isArray(v)) return [];
  if (v && typeof v === 'object') {
    const o: Record<string, unknown> = {};
    for (const k of Object.keys(v as object)) o[k] = emptyLike((v as any)[k]);
    return o;
  }
  if (typeof v === 'number') return 0;
  if (typeof v === 'boolean') return false;
  return '';
}

function Field({ fieldKey, value, onChange }: { fieldKey: string; value: unknown; onChange: (v: unknown) => void }) {
  const label = humanize(fieldKey);

  if (typeof value === 'boolean') {
    return (
      <FormControlLabel
        control={<Switch checked={value} onChange={(e) => onChange(e.target.checked)} />}
        label={label}
        sx={{ mb: 1 }}
      />
    );
  }

  if (typeof value === 'number') {
    return (
      <TextField
        fullWidth size="small" type="number" label={label} value={value}
        onChange={(e) => onChange(Number(e.target.value))} sx={{ mb: 2 }}
      />
    );
  }

  if (Array.isArray(value)) {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
          {label} ({value.length})
        </Typography>
        <Stack spacing={1} sx={{ mt: 0.5 }}>
          {value.map((item, i) => (
            <Paper key={i} variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Item {i + 1}
                </Typography>
                <Box>
                  <IconButton size="small" disabled={i === 0} onClick={() => { const n = [...value]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; onChange(n); }}><ArrowUpwardIcon fontSize="inherit" /></IconButton>
                  <IconButton size="small" disabled={i === value.length - 1} onClick={() => { const n = [...value]; [n[i + 1], n[i]] = [n[i], n[i + 1]]; onChange(n); }}><ArrowDownwardIcon fontSize="inherit" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => onChange(value.filter((_, j) => j !== i))}><DeleteOutlineIcon fontSize="inherit" /></IconButton>
                </Box>
              </Stack>
              <Field
                /* Object items render flat (fieldKey=""); primitive items inherit
                   the array's key so image/long-text detection still works — e.g.
                   an `images: string[]` array gets the image picker per item. */
                fieldKey={item !== null && typeof item === 'object' ? '' : fieldKey}
                value={item}
                onChange={(v) => { const n = [...value]; n[i] = v; onChange(n); }}
              />
            </Paper>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={() => onChange([...value, value.length ? emptyLike(value[0]) : ''])} sx={{ alignSelf: 'flex-start' }}>
            Add {humanize(fieldKey).replace(/s$/, '')}
          </Button>
        </Stack>
      </Box>
    );
  }

  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const body = Object.keys(obj).map((k) => (
      <Field key={k} fieldKey={k} value={obj[k]} onChange={(v) => onChange({ ...obj, [k]: v })} />
    ));
    // Array items pass fieldKey="" → render flat; named objects get a titled group.
    return fieldKey ? (
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>{label}</Typography>
        <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, bgcolor: 'action.hover' }}>{body}</Paper>
      </Box>
    ) : (
      <>{body}</>
    );
  }

  const str = value == null ? '' : String(value);
  const isImage = isImageKey(fieldKey);
  const asImage = isImage && /^(https?:\/\/|\/)/.test(str);
  const picker = useImagePicker();
  return (
    <Box sx={{ mb: 2 }}>
      <TextField
        fullWidth size="small"
        label={label + (isImage ? ' (image URL)' : '')}
        value={str}
        multiline={isLongKey(fieldKey) && !isImage}
        minRows={isLongKey(fieldKey) && !isImage ? 3 : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      {isImage && picker ? (
        <Button size="small" startIcon={<PhotoLibraryIcon />} sx={{ mt: 0.5 }}
          onClick={async () => { const url = await picker.pick(); if (url) onChange(url); }}>
          {str ? 'Change image' : 'Browse / upload'}
        </Button>
      ) : null}
      {asImage ? (
        <Box component="img" src={str} alt="" sx={{ display: 'block', maxHeight: 72, mt: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
      ) : null}
    </Box>
  );
}

export function PropForm({ props, onChange }: { props: Record<string, unknown>; onChange: (p: Record<string, unknown>) => void }) {
  const keys = Object.keys(props);
  if (!keys.length) return <Typography variant="body2" color="text.secondary">This section has no editable fields.</Typography>;
  return (
    <>
      {keys.map((k) => (
        <Field key={k} fieldKey={k} value={props[k]} onChange={(v) => onChange({ ...props, [k]: v })} />
      ))}
    </>
  );
}
